import { Provider } from "react-redux";
import { MsalProvider } from "@azure/msal-react";
import { createBrowserRouter, createRoutesFromElements, RouterProvider, Route, Navigate } from "react-router-dom";
import { store } from "./store";
import { msalInstance } from "./auth/msalConfig";
import { ProtectedRoute } from "./auth/ProtectedRoute";
import { AppointmentsRoute } from "./auth/AppointmentsRoute";
import { AppointmentsManageRoute } from "./auth/AppointmentsManageRoute";
import { ErrorBoundary } from "./components/error-boundary";
import "./index.css";

import LoginPage from "./app/login/page";
import { BookingFlowPage } from "./app/booking-flow/page";
import DocumentUploadPage from "./app/document-upload/page";
import ApplicationStatusPage from "./app/application-status/page";
import BookingLandingPage from "./app/booking-landing/page";
import BookingManagePage from "./app/booking-manage/page";
import BookingCancelPage from "./app/booking-cancel/page";
import SmsTermsPage from "./app/sms-terms/page";
import PrivacyPolicyPage from "./app/privacy/page";
import AppLayout from "./components/app-layout";
import DashboardPage from "./app/dashboard/page";
import TeamPerformancePage from "./app/team-performance/page";
import FundingSourcesPage from "./app/funding-sources/page";
import BenefitProgramsPage from "./app/benefit-programs/page";
import BenefitApplicationsPage from "./app/benefit-applications/page";
import PseOnlinePage from "./app/pse-online/page";
import PseOnlineDetailPage from "./app/pse-online/detail/page";
import PseSnapshotImportPage from "./app/pse-online/snapshot-import/page";
import AppointmentsPage from "./app/booking-calendar/page";
import BookingReviewPage from "./app/appointments/review/page";
import UnassignedHostsPage from "./app/appointments/unassigned-hosts/page";
import DataGapsPage from "./app/appointments/data-gaps/page";
import IncompleteBookingsPage from "./app/appointments/incomplete/page";
import ScheduleOverridesPage from "./app/appointments/schedule-overrides/page";
import { ScheduleOverridesRoute } from "./app/appointments/schedule-overrides/ScheduleOverridesRoute";
import CancellationsPage from "./app/appointments/cancellations/page";
import BookingSeasonPage from "./app/appointments/booking-season/page";
import FundingSwitchesPage from "./app/appointments/funding-switches/page";
import AppointmentSearchPage from "./app/appointments/search/page";
import AvailabilityPage from "./app/appointments/availability/page";
import ManualReschedulePage from "./app/appointments/availability/resolve/page";
import StaffMovesPage from "./app/appointments/staff-moves/page";
import SettingsPage from "./app/settings/page";
import SearchPage from "./app/search/page";
import BenefitRunsPage from "./app/benefit-runs/page";
import PledgeReportsPage from "./app/pledge-reports/page";
import LegacyPledgeReportsPage from "./app/pledge-reports/legacy/page";
import AsciiExportsPage from "./app/ascii-exports/page";
import BenefitDetailPage from "./app/benefit-applications/detail/page";
import NewBenefitApplicationPage from "./app/benefit-applications/new/page";
import AdminUsersPage from "./app/admin/users/page";
import AdminUserDetailPage from "./app/admin/users/detail/page";
import IntakeCatalogPage from "./app/admin/intake-catalog/page";
import BenefitProgramDetailPage from "./app/benefit-programs/detail/page";
import BenefitCalculatorPage from "./app/benefit-calculator/page";
import RecertificationPage from "./app/recertification/page";
import PeoplePage from "./app/people/page";
import PersonDetailPage from "./app/people/detail/page";
import ChangeRequestsPage from "./app/change-requests/page";
import AdminReviewersPage from "./app/admin/reviewers/page";
import { Toaster } from "./components/ui/sonner";

// A data router provides supported navigation blocking for unfinished intake editors.
// Route paths and the authentication/provider hierarchy remain unchanged.
const router = createBrowserRouter(createRoutesFromElements(
            <>
              <Route path="/booking" element={<BookingLandingPage />} />
              <Route path="/booking/new" element={<BookingFlowPage />} />
              <Route path="/booking/manage" element={<BookingManagePage />} />
              <Route path="/booking/cancel" element={<BookingCancelPage />} />
              <Route path="/booking/document-upload" element={<DocumentUploadPage />} />
              <Route path="/booking/application-status" element={<ApplicationStatusPage />} />
              {/* Linked from the SMS opt-in. Must stay outside ProtectedRoute: carrier review follows these links without an account. */}
              <Route path="/booking/sms-terms" element={<SmsTermsPage />} />
              <Route path="/booking/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route
                element={
                  <ProtectedRoute>
                    <AppLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/team-performance" element={<TeamPerformancePage />} />
                <Route path="/funding-sources" element={<FundingSourcesPage />} />
                <Route path="/benefit-programs" element={<BenefitProgramsPage />} />
                <Route path="/benefit-programs/:id" element={<BenefitProgramDetailPage />} />
                <Route path="/benefit-applications" element={<BenefitApplicationsPage />} />
                <Route path="/benefit-applications/new" element={<NewBenefitApplicationPage />} />
                <Route path="/benefit-applications/:id" element={<BenefitDetailPage />} />
                <Route path="/people" element={<PeoplePage />} />
                <Route path="/people/:id" element={<PersonDetailPage />} />
                <Route path="/change-requests" element={<ChangeRequestsPage />} />
                <Route path="/benefit-runs" element={<BenefitRunsPage />} />
                <Route path="/benefit-runs/:runId" element={<BenefitRunsPage />} />
                <Route path="/pledge-reports" element={<PledgeReportsPage />} />
                <Route path="/pledge-reports/legacy" element={<LegacyPledgeReportsPage />} />
                <Route path="/ascii-exports" element={<AsciiExportsPage />} />
                <Route path="/benefit-calculator" element={<BenefitCalculatorPage />} />
                <Route path="/pse-online" element={<PseOnlinePage />} />
                <Route path="/pse-online/snapshot-import" element={<PseSnapshotImportPage />} />
                <Route path="/pse-online/:id" element={<PseOnlineDetailPage />} />
                <Route element={<AppointmentsRoute />}>
                  <Route path="/appointments" element={<AppointmentsPage />} />
                  <Route path="/appointments/review" element={<BookingReviewPage />} />
                  <Route path="/appointments/unassigned-hosts" element={<UnassignedHostsPage />} />
                  <Route path="/appointments/data-gaps" element={<DataGapsPage />} />
                  <Route path="/appointments/incomplete" element={<IncompleteBookingsPage />} />
                  {/* Manage only (AB#842): view is sent to /appointments. For manage, redirects to Availability
                      (keeping ?site=) when AmsAvailabilityWeekView is on (AB#807). */}
                  <Route
                    path="/appointments/schedule-overrides"
                    element={<AppointmentsManageRoute><ScheduleOverridesRoute><ScheduleOverridesPage /></ScheduleOverridesRoute></AppointmentsManageRoute>}
                  />
                  <Route path="/appointments/cancellations" element={<CancellationsPage />} />
                  <Route path="/appointments/booking-season" element={<BookingSeasonPage />} />
                  <Route path="/appointments/funding-switches" element={<FundingSwitchesPage />} />
                  <Route path="/appointments/search" element={<AppointmentSearchPage />} />
                  {/* Manage only (AB#842): view is sent to /appointments. */}
                  <Route path="/appointments/availability" element={<AppointmentsManageRoute><AvailabilityPage /></AppointmentsManageRoute>} />
                  {/* Manage only, checked by the page; reached from an override's resolution choice (AB#811). */}
                  <Route path="/appointments/availability/resolve/:overrideId" element={<ManualReschedulePage />} />
                  <Route path="/appointments/staff-moves" element={<StaffMovesPage />} />
                </Route>
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/search" element={<SearchPage />} />
                <Route path="/admin/users" element={<AdminUsersPage />} />
                <Route path="/admin/users/:userId" element={<AdminUserDetailPage />} />
                <Route path="/admin/intake-catalog" element={<IntakeCatalogPage />} />
                <Route path="/admin/reviewers" element={<AdminReviewersPage />} />
                <Route path="/recertification" element={<RecertificationPage />} />
              </Route>
            </>
));

export function App() {
  return (
    <ErrorBoundary>
      <Provider store={store}>
        <MsalProvider instance={msalInstance}>
          <RouterProvider router={router} />
          <Toaster />
        </MsalProvider>
      </Provider>
    </ErrorBoundary>
  );
}

export default App;
